import React from 'react';
import { Card, Descriptions, Tag } from 'antd';
import { FileText, User } from 'lucide-react';
import { safeJSONParse } from '@/utils/helper';

interface IntakeResponseTabProps {
	intakeResponses: string;
}

const IntakeResponseTab: React.FC<IntakeResponseTabProps> = ({ intakeResponses }) => {
	const [parsedData, setParsedData] = React.useState<any>(null);
	const [error, setError] = React.useState<string | null>(null);

	React.useEffect(() => {
		try {
			const parsed = safeJSONParse(intakeResponses, []);
			setParsedData(parsed);
			setError(null);
		} catch (e) {
			setError('Failed to parse intake response data');
			console.error('Error parsing intake responses:', e);
			setParsedData(null);
		}
	}, [intakeResponses]);

	const renderValue = (value: any) => {
		if (typeof value === 'boolean') {
			return <Tag color={value ? 'success' : 'error'}>{value ? 'Yes' : 'No'}</Tag>;
		}
		if (typeof value === 'object' && value !== null) {
			return <pre className="text-xs bg-gray-50 p-2 rounded">{JSON.stringify(value, null, 2)}</pre>;
		}
		if (Array.isArray(value)) {
			return (
				<div className="space-y-1">
					{value.map((item, index) => (
						<Tag key={index} className="mb-1">
							{String(item)}
						</Tag>
					))}
				</div>
			);
		}
		return String(value || 'N/A');
	};

	const renderFormResponse = (data: any, index?: number) => {
		const title = index !== undefined ? `Response ${index + 1}` : 'Intake Response';

		return (
			<Card key={index} className="mb-4" size="small">
				<div className="flex items-center gap-2 mb-4">
					<FileText className="w-5 h-5" />
					<h4 className="text-base font-medium">{title}</h4>
				</div>

				<Descriptions column={1} size="small" bordered>
					{Object.entries(data).map(([key, value]) => (
						<Descriptions.Item
							key={key}
							label={
								<span className="font-medium capitalize">
									{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
								</span>
							}
						>
							{renderValue(value)}
						</Descriptions.Item>
					))}
				</Descriptions>
			</Card>
		);
	};

	if (error) {
		return (
			<div className="text-center py-8">
				<div className="text-4xl text-red-400 mb-4">⚠️</div>
				<h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Intake Response</h3>
				<p className="text-red-500 mb-4">{error}</p>
				<div className="text-xs bg-red-50 p-3 rounded border border-red-200">
					<pre>{intakeResponses}</pre>
				</div>
			</div>
		);
	}

	if (!parsedData) {
		return (
			<div className="text-center py-8">
				<div className="text-4xl text-gray-400 mb-4">📝</div>
				<h3 className="text-lg font-medium text-gray-900 mb-2">Loading Intake Response...</h3>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Header */}
			<div className="flex items-center gap-2 mb-6">
				<User className="w-6 h-6" />
				<h3 className="text-xl font-semibold">Intake Form Response</h3>
			</div>

			{/* Response Data */}
			{Array.isArray(parsedData) ? (
				parsedData.length > 0 ? (
					parsedData.map((response, index) => renderFormResponse(response, index))
				) : (
					<div className="text-center py-8">
						<div className="text-4xl text-gray-400 mb-4">📝</div>
						<p className="text-gray-500">No intake responses found</p>
					</div>
				)
			) : typeof parsedData === 'object' && parsedData !== null ? (
				renderFormResponse(parsedData)
			) : (
				<div className="text-center py-8">
					<div className="text-4xl text-gray-400 mb-4">📝</div>
					<p className="text-gray-500">Invalid intake response format</p>
					<div className="text-xs bg-gray-50 p-3 rounded border mt-4">
						<pre>{JSON.stringify(parsedData, null, 2)}</pre>
					</div>
				</div>
			)}
		</div>
	);
};

export default IntakeResponseTab;
